import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

/* ---- Checklist helper ---- */
const Checklist: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="hp-showcase-checklist">
    {items.map((item, i) => (
      <li key={i}>
        <span className="hp-check-icon">✓</span>
        {item}
      </li>
    ))}
  </ul>
);

/* ---- Row 1: Coaching-Prozess Klient-Karte ---- */
const CoachingProcessMockup: React.FC = () => {
  const { t } = useTranslation();
  const mockup = t('platform.mockup', { returnObjects: true }) as any;
  const processSteps = mockup.processSteps as { label: string; date: string }[];

  const icons = ['💬', '📋', '📚', '🔄'];
  const statuses = ['done', 'done', 'active', 'pending'];
  const bgs = ['#dcfce7', '#dbeafe', '#ede9fe', '#f1f5f9'];

  return (
    <div className="hp-showcase-visual">
      <div className="hp-mock-header">
        <span className="hp-mock-title">Maria K.</span>
        <span className="hp-mock-badge hp-mock-badge--blue">10er Executive</span>
      </div>
      <div className="hp-timeline">
        {processSteps.map((step, i) => (
          <div key={i} className={`hp-timeline-step hp-timeline-step--${statuses[i]}`}>
            <div className="hp-timeline-dot" style={{ background: bgs[i] }} aria-hidden="true">{icons[i]}</div>
            <div className="hp-timeline-content">
              <span className="hp-timeline-label">{step.label}</span>
              <span className="hp-timeline-date">{step.date}</span>
            </div>
            <span className={`hp-timeline-status hp-timeline-status--${statuses[i]}`}>
              {statuses[i] === 'done' && mockup.statusDone}
              {statuses[i] === 'active' && mockup.statusActive}
              {statuses[i] === 'pending' && mockup.statusPending}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---- Row 2: Rechnungstabelle ---- */
const InvoiceTableMockup: React.FC = () => {
  const { t } = useTranslation();
  const mockup = t('platform.mockup', { returnObjects: true }) as any;
  const invoiceTh = mockup.invoiceTh as string[];
  const invoiceStatuses = mockup.invoiceStatuses as string[];

  const rows = [
    { nr: 'RE-2026-0047', color: '#3b82f6', initials: 'MK', name: 'Maria K.', amount: '€ 1.200,00', statusClass: 'green', statusIcon: '✅' },
    { nr: 'RE-2026-0048', color: '#8b5cf6', initials: 'TS', name: 'Thomas S.', amount: '€ 890,00', statusClass: 'blue', statusIcon: '📤' },
    { nr: 'RE-2026-0046', color: '#f59e0b', initials: 'AB', name: 'Anna B.', amount: '€ 450,00', statusClass: 'amber', statusIcon: '⚠️' },
  ];

  return (
    <div className="hp-showcase-visual">
      <div className="hp-mock-header">
        <span className="hp-mock-title">{mockup.invoiceTitle}</span>
        <span className="hp-mock-badge hp-mock-badge--green">{mockup.invoiceQuarter}</span>
      </div>
      <div className="hp-mock-table">
        <div className="hp-mock-table-head">
          {invoiceTh.map((th: string) => <span key={th}>{th}</span>)}
        </div>
        {rows.map((row, i) => (
          <div key={row.nr} className="hp-mock-table-row">
            <span className="hp-mock-table-nr">{row.nr}</span>
            <span className="hp-mock-table-client">
              <span className="hp-mock-avatar" style={{ background: row.color }}>{row.initials}</span>
              {row.name}
            </span>
            <span className="hp-mock-table-amount">{row.amount}</span>
            <span className={`hp-mock-table-status hp-mock-table-status--${row.statusClass}`}>
              {row.statusIcon} {invoiceStatuses[i]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---- Row 3: Wochenkalender ---- */
const WeekCalendarMockup: React.FC = () => {
  const { t } = useTranslation();
  const mockup = t('platform.mockup', { returnObjects: true }) as any;
  const days = mockup.calendarDays as string[];
  const slotLabels = mockup.calendarSlots as string[];

  const slots = [
    { day: 0, time: '09:00', label: slotLabels[0], color: 'blue' },
    { day: 1, time: '11:00', label: slotLabels[1], color: 'green' },
    { day: 2, time: '14:00', label: slotLabels[2], color: 'amber' },
    { day: 4, time: '09:00', label: slotLabels[3], color: 'purple' },
  ];

  return (
    <div className="hp-showcase-visual">
      <div className="hp-mock-header">
        <span className="hp-mock-title">{mockup.calendarTitle}</span>
        <span className="hp-mock-badge hp-mock-badge--blue">{mockup.calendarBadge}</span>
      </div>
      <div className="hp-mock-calendar">
        <div className="hp-mock-cal-header">
          <span className="hp-mock-cal-time-spacer" />
          {days.map((d) => (
            <span key={d} className="hp-mock-cal-day">{d}</span>
          ))}
        </div>
        {['09:00', '11:00', '14:00'].map((time) => (
          <div key={time} className="hp-mock-cal-row">
            <span className="hp-mock-cal-time">{time}</span>
            {days.map((_, di) => {
              const slot = slots.find((s) => s.day === di && s.time === time);
              return (
                <div key={di} className="hp-mock-cal-cell">
                  {slot && (
                    <div className={`hp-mock-cal-event hp-mock-cal-event--${slot.color}`}>
                      {slot.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---- Main Section ---- */
const PlatformSection: React.FC = () => {
  const { t } = useTranslation();
  const headerRef = useScrollAnimation<HTMLDivElement>();
  const row1Ref = useScrollAnimation<HTMLDivElement>(0.15);
  const row2Ref = useScrollAnimation<HTMLDivElement>(0.15);
  const row3Ref = useScrollAnimation<HTMLDivElement>(0.15);

  const row1 = t('platform.row1', { returnObjects: true }) as any;
  const row2 = t('platform.row2', { returnObjects: true }) as any;
  const row3 = t('platform.row3', { returnObjects: true }) as any;

  return (
    <div id="platform">
      {/* Section Header */}
      <div style={{ background: 'var(--hp-gray-50)', padding: '80px 48px 0', textAlign: 'center' }}>
        <div ref={headerRef} className="hp-section-header hp-animate" style={{ marginBottom: 0 }}>
          <span className="hp-section-label">{t('platform.sectionLabel')}</span>
          <h2>{t('platform.title')}</h2>
          <p>{t('platform.subtitle')}</p>
        </div>
      </div>

      {/* Row 1: Hell — Coaching-Prozesse */}
      <div className="hp-platform-row">
        <div ref={row1Ref} className="hp-platform-row-inner hp-animate">
          <div className="hp-platform-text">
            <span className="hp-platform-claim-badge">{row1.badge}</span>
            <h3>{row1.title}</h3>
            <p>{row1.description}</p>
            <Checklist items={row1.checklist} />
          </div>
          <CoachingProcessMockup />
        </div>
      </div>

      {/* Row 2: Dunkel — Rechnungen (reverse) */}
      <div className="hp-platform-row hp-platform-row--dark">
        <div ref={row2Ref} className="hp-platform-row-inner hp-platform-row--reverse hp-animate">
          <div className="hp-platform-text">
            <span className="hp-platform-claim-badge">{row2.badge}</span>
            <h3>{row2.title}</h3>
            <p>{row2.description}</p>
            <Checklist items={row2.checklist} />
          </div>
          <InvoiceTableMockup />
        </div>
      </div>

      {/* Row 3: Hell — Termine */}
      <div className="hp-platform-row">
        <div ref={row3Ref} className="hp-platform-row-inner hp-animate">
          <div className="hp-platform-text">
            <span className="hp-platform-claim-badge">{row3.badge}</span>
            <h3>{row3.title}</h3>
            <p>{row3.description}</p>
            <Checklist items={row3.checklist} />
          </div>
          <WeekCalendarMockup />
        </div>
      </div>
    </div>
  );
};

export default PlatformSection;
